import type { Database } from 'node-sqlite3-wasm';
import {
  CodeNavCacheUnavailableError,
  getCodeNavCacheInitPromise,
  isCodeNavCacheUnavailable,
} from './code-nav-cache-availability.js';
import {
  codeNavCacheSqlite,
  withCodeNavCacheSqliteSyncWrapped,
  withCodeNavCacheSqliteWorkLockProbeWrapped,
  withCodeNavCacheSqliteWorkLockWrapped,
} from './instance.js';

export { CliSqliteInterrupted } from '../sqlite-errors.js';

export const safeCloseCodeNavCacheSqliteDatabase = codeNavCacheSqlite.safeClose;

export {
  closeAllCodeNavCacheSqliteConnections,
  closeCodeNavCacheDatabaseForTests,
} from './instance.js';

export const openCodeNavCacheSqliteConnection = codeNavCacheSqlite.openConnection;

export async function withCodeNavCacheSqlite<T>(
  fn: (db: Database) => T | Promise<T>,
): Promise<T> {
  if (isCodeNavCacheUnavailable()) throw new CodeNavCacheUnavailableError();
  const initPromise = getCodeNavCacheInitPromise();
  if (initPromise != null) {
    const ok = await initPromise;
    if (!ok) throw new CodeNavCacheUnavailableError();
  }

  return withCodeNavCacheSqliteWorkLockWrapped(fn);
}

/** Fast SQLite probe for interactive paths; returns undefined when locked or unavailable. */
export async function withCodeNavCacheSqliteProbe<T>(
  fn: (db: Database) => T | Promise<T>,
): Promise<T | undefined> {
  if (isCodeNavCacheUnavailable()) return undefined;
  const initPromise = getCodeNavCacheInitPromise();
  if (initPromise != null) {
    const ok = await initPromise;
    if (!ok) return undefined;
  }

  return withCodeNavCacheSqliteWorkLockProbeWrapped(fn);
}

/**
 * Prefer {@link withCodeNavCacheSqlite}. Serialized with async access; reentrant on nested calls.
 */
export function withCodeNavCacheSqliteSync<T>(fn: (db: Database) => T): T {
  if (isCodeNavCacheUnavailable()) throw new CodeNavCacheUnavailableError();
  return withCodeNavCacheSqliteSyncWrapped(fn);
}

export async function ensureCodeNavCacheSqliteInitialized(): Promise<void> {
  await codeNavCacheSqlite.withAsync(() => undefined);
}

export { startCodeNavCacheInitialization, awaitCodeNavCacheInitialization } from './code-nav-cache-availability.js';
export { CodeNavCacheUnavailableError } from './code-nav-cache-availability.js';
